<template>
  <div :data-testid="'deviceType-' + (deviceTypeValue?.label ?? entityType)">
    <div v-if="devices.length > 0">
      <div class="mb-3 inline-flex items-center">
        <div v-if="!deviceTypeValue">
          <GatewayStatusBadge
            :status="entityType"
            :is-selected="hasInstallationLocation"
          />
          <span class="ml-2 lg:text-lg">
            {{ deviceTypeDetails[DeviceTypeEnum.LO_RA_WAN_GATEWAY]?.label }}
          </span>
        </div>
        <div v-else>
          <BaseChip
            :text="deviceTypeValue.shortLabel"
            :number="devices.length"
            :custom-color="deviceTypeValue.color"
          />
          <span class="ml-2 lg:text-lg">
            {{ deviceTypeValue.label }}
          </span>
        </div>
      </div>

      <table
        class="m-0! w-full rounded-xl text-left dark:bg-dark-3"
        aria-label="Installed Devices"
        data-testid="installed_devices_table"
      >
        <thead class="bg-light-0 text-xs uppercase dark:bg-dark-2">
          <tr>
            <th scope="col" class="rounded-tl-xl py-3 pl-6">
              {{ isPlant ? 'Notizen' : 's/N' }}
            </th>
            <th scope="col" class="py-3">
              {{ !isPlant && !isLegacyPlantDevice ? 'Artikelnummer' : '' }}
            </th>
            <th scope="col" class="rounded-tr-xl px-6 py-3 text-right"></th>
          </tr>
        </thead>
        <tbody>
          <DeviceTypeTableElement
            v-for="(device, index) in devices"
            :key="device.id"
            :device="device"
            :is-plant="isPlant"
            :is-last="index === devices.length - 1"
            :on-edit="onEdit"
            :on-unmount="onUnmount"
            :on-delete="onDelete"
            :on-exchange="onExchange"
            :on-tkd-service="onTkdService"
            :show-funkcheck-status="
              hasDmgw6FunkcheckStatuses &&
              isFunkcheckCompatibleType &&
              isFunkcheckCompatibleDevice(device)
            "
            :data-testid="
              isPlant ? deviceTypeValue?.value : deviceEntryTestId(device)
            "
          />
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import DeviceTypeTableElement from './DeviceTypeTableElement.vue';
import { useDmgw6Store } from '@/store/dmgw6/dmgw6Store';
import BaseChip from '@/components/UI/Chip/BaseChip.vue';
import GatewayStatusBadge from '@/components/LoRaWanRadioCheck/Badges/GatewayStatusBadge.vue';

import {
  deviceTypeDetails,
  DeviceTypeEnum,
  deviceTypes,
  inspectionDeviceTypeDetails,
  submeteringDeviceTypes,
} from '@/enums/device';
import { PlantType, plantTypeDetails } from '@/models/plant/Plant';
import { Device } from '@/models/devices/Device';
import type { DeviceClasses } from '@/models/devices/Device';
import type { InstallationPoint } from '@/models/installationPoint/InstallationPoint';
import {
  InspectionDevice,
  InspectionDeviceType,
} from '@/models/inspectionPoint/InspectionDevice';
import { useInspectionPointStore } from '@/store/entities/inspectionPointStore';
import { useInstallationPointStore } from '@/store/entities/installationPointStore';

const props = defineProps<{
  entityType: string;
  entities: Array<DeviceClasses | InspectionDevice>;
  onEdit: (installationPoint: InstallationPoint) => void;
  onUnmount?: (installationPoint: InstallationPoint) => void;
  onDelete?: (installationPoint: InstallationPoint) => void;
  onExchange: (installationPoint: InstallationPoint) => void;
  onTkdService: (installationPoint: InstallationPoint) => void;
}>();

const inspectionPointStore = useInspectionPointStore();
const installationPointStore = useInstallationPointStore();

const hasDmgw6FunkcheckStatuses = computed(
  () => useDmgw6Store().hasFunkcheckStatuses
);

const isFunkcheckCompatibleType = computed(() =>
  submeteringDeviceTypes.includes(props.entityType as DeviceTypeEnum)
);

const isFunkcheckCompatibleDevice = (
  device: Device<any> | InspectionDevice
): boolean => device instanceof Device && device.isAutomatedMeterReading;

const hasInstallationLocation = computed(() =>
  props.entities.some(
    (device) =>
      device instanceof InspectionDevice &&
      inspectionPointStore.getInspectionPointById(device.inspectionPointId)
        ?.inspectionData.isInstallationLocation
  )
);

const isPlant = computed(() =>
  Object.values(PlantType).includes(props.entityType as PlantType)
);

const deviceTypeValue = computed(() => {
  if (props.entities[0] instanceof InspectionDevice) {
    return inspectionDeviceTypeDetails[
      props.entityType as InspectionDeviceType
    ];
  }

  if (isPlant.value) {
    return plantTypeDetails[props.entityType as PlantType];
  }

  return deviceTypeDetails[props.entityType as DeviceTypeEnum];
});

const sortDevicesByOrdinal = (devices: DeviceClasses[]) => {
  return [...devices].sort((a, b) => {
    const ordinalA =
      installationPointStore.getInstallationPointById(a.installationPointId)
        ?.ordinal ?? 0;
    const ordinalB =
      installationPointStore.getInstallationPointById(b.installationPointId)
        ?.ordinal ?? 0;
    return ordinalA - ordinalB;
  });
};

const devices = computed<(Device<any> | InspectionDevice)[]>(() => {
  if (props.entities[0] instanceof Device) {
    return sortDevicesByOrdinal(props.entities as DeviceClasses[]);
  }
  return props.entities as InspectionDevice[];
});

const isLegacyPlantDevice = computed(
  () =>
    props.entityType === deviceTypes.LegacyHeatingPlantMeter ||
    props.entityType === deviceTypes.LegacyWaterPlantMeter
);

const deviceEntryTestId = (device: Device<any> | InspectionDevice) => {
  const typeLabel = deviceTypeValue.value?.label || '';
  const identifier = device.serialNumber
    ? `-sn-${device.serialNumber}`
    : `-an-${device.productKey}`;
  return `${typeLabel}${identifier}`;
};
</script>

<style scoped></style>
